<template>
    <div id="wallet-settings">
        <WalletAddBalance v-if="addBalanceModal" :is-admin="isAdmin" @add-balance="addBalanceWhatsapp" @close="addBalanceModal = false" />
        <CreditCardNeeded @close="creditCardNeededModal = false" v-if="creditCardNeededModal"/>
        <AutoRebalancePromoModal
            v-if="showAutoRebalancePromo"
            @activate="handleAutoRebalancePromoActivate"
            @close="handleAutoRebalancePromoClose"
        />

        <!-- 1) Balance Card -->
        <div class="balance-card">
            <div class="balance-left">
                <div class="balance-icon">
                    <span class="material-icons">account_balance_wallet</span>
                </div>
                <div class="balance-text">
                    <span class="balance-label">{{ $t("billing.availableBalance") }}</span>
                    <span class="balance-amount" v-if="wallet.whatsapp">{{ formatPrice(wallet.whatsapp.amount) }} {{ $t("billing.currencyTL") }}</span>
                </div>
            </div>
            <SupsisButton :primary="true" icon="add" @click="openAddWhatsappBalanceModal">
                {{ $t("billing.addBalance") }}
            </SupsisButton>
        </div>

        <!-- 2) Underline Tabs -->
        <div class="underline-tabs">
            <div
                class="u-tab"
                :class="{ active: activeMainTab === 'transactions' }"
                @click="activeMainTab = 'transactions'"
            >
                <span class="material-icons">receipt_long</span>
                {{ $t("billing.walletTransactions") }}
            </div>
            <div
                class="u-tab"
                :class="{ active: activeMainTab === 'auto-rebalance' }"
                @click="activeMainTab = 'auto-rebalance'"
            >
                <span class="material-icons">autorenew</span>
                {{ $t("billing.autoReload") }}
            </div>
        </div>

        <!-- ============ TRANSACTIONS ============ -->
        <div v-if="activeMainTab === 'transactions'" :key="$i18n.locale" class="tx-panel">

            <!-- 3) Filter Bar -->
            <div class="filter-bar">
                <div class="product-pills">
                    <div
                        v-for="p in productOptions"
                        :key="p.value"
                        class="pill"
                        :class="{ active: activeTab === p.value }"
                        @click="onProductChange(p.value)"
                    >{{ p.label }}</div>
                </div>

                <!-- tarih, kanal ve pagination artik table-toolbar'da -->
            </div>

            <!-- type + ref filters moved to toolbar as FilterChip -->

            <!-- Table Toolbar -->
            <div class="table-toolbar">
                <div class="toolbar-left">
                    <FilterChip
                        type="date"
                        :value="dateFilterValue"
                        :filter-name="$t('message.date') || 'Tarih'"
                        icon="date_range"
                        :clearable="true"
                        :operator-selectable="true"
                        :operator="dateOperator"
                        @change="onDateFilterChange"
                        @operator-change="onDateOperatorChange"
                        @clear="onDateFilterClear"
                    />
                    <FilterChip
                        v-if="typeOptions.length > 0"
                        type="select"
                        :value="selectedTypes"
                        :options="typeOptions"
                        :filter-name="$t('billing.transactionTypes') || 'İşlem Tipi'"
                        :multiple="true"
                        :enable-search="true"
                        icon="category"
                        :clearable="true"
                        @change="onTypesFilterChange"
                        @clear="onTypesFilterClear"
                    />
                    <FilterChip
                        v-if="activeTab === 'whatsapp-business-api'"
                        type="select"
                        :value="selectedChannelIds"
                        :options="channelFilterOptions"
                        :filter-name="$t('message.channel') || 'Kanal'"
                        :multiple="true"
                        :enable-search="true"
                        icon="campaign"
                        :clearable="true"
                        @change="onChannelFilterChange"
                        @clear="onChannelFilterClear"
                    />
                    <FilterChip
                        v-if="activeTab === 'ai-usage' && refOptions.length > 0"
                        type="select"
                        :value="selectedRefs"
                        :options="refOptions"
                        :filter-name="$t('message.resource') || 'Kaynak'"
                        :multiple="true"
                        :enable-search="true"
                        icon="psychology"
                        :clearable="true"
                        @change="onRefsChange"
                        @clear="onRefsClear"
                    />
                </div>
                <div class="toolbar-right">
                    <span class="pg-info">
                        {{ filters.perPage * (filters.page - 1) + 1 }}–{{ Math.min(filters.perPage * filters.page, totalActivity) }}
                        / {{ totalActivity }} {{ $t("message.records") }}
                    </span>
                    <div class="pg-controls">
                        <SupsisButton :ghost="true" :small="true" :square="true" :disabled="filters.page <= 1" icon="chevron_left" @click="backwardPage" />
                        <a-input-number v-model="currentPageInput" :min="1" :max="totalPages" @pressEnter="jumpToPage" @blur="jumpToPage" class="pg-input" />
                        <span class="pg-of">/ {{ totalPages }}</span>
                        <SupsisButton :ghost="true" :small="true" :square="true" :disabled="filters.page >= totalPages" icon="chevron_right" @click="forwardPage" />
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div class="table-area">
                <NewSupsisTable
                    :key="activeTab"
                    :columns="tableColumns"
                    :data="activities"
                    :show-column-add="true"
                    :resizable="true"
                    :sortable-columns="true"
                    :local-storage-key="'wallet-table-' + activeTab"
                    row-key="_id"
                >
                    <template v-slot:cell-createdAt="{ row }">
                        <span class="cell-date">{{ formatDate(row.createdAt) }}</span>
                    </template>
                    <template v-slot:cell-type="{ row }">
                        {{ formatType(row.type, row) }}
                    </template>
                    <template v-slot:cell-aiResource="{ row }">
                        <span class="ai-resource">{{ getAIUsageResource(row) }}</span>
                    </template>
                    <template v-slot:cell-phoneNumber="{ row }">{{ getPhoneNumber(row) }}</template>
                    <template v-slot:cell-country="{ row }">{{ $_.get(row, "payload.country", "-") }}</template>
                    <template v-slot:cell-fromNumber="{ row }">{{ $_.get(row, "payload.from_number", "-") }}</template>
                    <template v-slot:cell-toNumber="{ row }">{{ $_.get(row, "payload.to_number", "-") }}</template>
                    <template v-slot:cell-direction="{ row }">
                        <span v-if="$_.get(row, 'payload.direction') === 'inbound'" class="dir-badge in"><span class="material-icons">call_received</span>{{ $t("message.inbound") || "Gelen" }}</span>
                        <span v-else-if="$_.get(row, 'payload.direction') === 'outbound'" class="dir-badge out"><span class="material-icons">call_made</span>{{ $t("message.outbound") || "Giden" }}</span>
                        <span v-else>-</span>
                    </template>
                    <template v-slot:cell-aiModel="{ row }">
                        <span class="model-chip">{{ $_.get(row, "payload.model", "-") }}</span>
                    </template>
                    <template v-slot:cell-aiProvider="{ row }">
                        <span v-if="row.type === 'ai-rag-rerank'">Voyage AI</span>
                        <span v-else>{{ $_.get(row, "payload.openrouterGeneration.provider_name", $_.get(row, "payload.provider", "-")) }}</span>
                    </template>
                    <template v-slot:cell-aiDetails="{ row }">
                        <div class="ai-detail-wrap">
                            <template v-if="row.type === 'ai-rag-rerank'">
                                <a-tag color="purple">{{ $t("billing.walletAiRerankTag") }}</a-tag>
                                <span v-if="$_.get(row, 'payload.inputTokens') > 0" class="tok">{{ formatTokenCount($_.get(row, 'payload.inputTokens')) }} {{ $t("billing.walletAiTokenSuffix") }}</span>
                            </template>
                            <template v-else>
                                <a-tag v-if="$_.get(row, 'payload.openrouterGeneration.num_search_results') > 0" color="orange">{{ $t("billing.walletAiWebSearchResults", { n: $_.get(row, "payload.openrouterGeneration.num_search_results") }) }}</a-tag>
                                <a-tag v-if="$_.get(row, 'payload.openrouterGeneration.num_media_prompt') > 0" color="purple">{{ $t("billing.walletAiMediaCount", { n: $_.get(row, "payload.openrouterGeneration.num_media_prompt") }) }}</a-tag>
                                <a-tag v-if="$_.get(row, 'payload.openrouterGeneration.num_input_audio_prompt') > 0" color="cyan">{{ $t("billing.walletAiAudioCount", { n: $_.get(row, "payload.openrouterGeneration.num_input_audio_prompt") }) }}</a-tag>
                                <a-tag v-if="$_.get(row, 'payload.openrouterGeneration.router')" color="geekblue">{{ $_.get(row, "payload.openrouterGeneration.router") === "openrouter/auto" ? $t("billing.walletAiAutoRouter") : $_.get(row, "payload.openrouterGeneration.router") }}</a-tag>
                                <span v-if="!$_.get(row, 'payload.openrouterGeneration')">-</span>
                            </template>
                        </div>
                    </template>
                    <template v-slot:cell-before="{ row }"><MoneyFormatter :price="row.before.toFixed(4)" /></template>
                    <template v-slot:cell-after="{ row }"><MoneyFormatter :price="row.after.toFixed(4)" /></template>
                    <template v-slot:cell-amount="{ row }"><MoneyFormatter :price="row.amount.toFixed(4)" /></template>
                </NewSupsisTable>
            </div>

            <!-- empty: pagination moved to table-toolbar -->
        </div>

        <!-- ============ AUTO REBALANCE ============ -->
        <div v-if="activeMainTab === 'auto-rebalance'" class="ar-panel">
            <div class="ar-alert">
                <span class="material-icons ar-alert-icon">info</span>
                <div class="ar-alert-body">
                    <div class="ar-alert-title">{{ $t("billing.importantInfo") }}</div>
                    <div class="ar-points">
                        <div class="ar-point"><span>⚠️</span><span>{{ $t("billing.walletImportantPoint1") }}</span></div>
                        <div class="ar-point"><span>⏰</span><span>{{ $t("billing.walletImportantPoint2") }}</span></div>
                        <div class="ar-point"><span>🔔</span><span>{{ $t("billing.walletImportantPoint3Before") }}<router-link to="/settings/notifications" class="ar-link">{{ $t("billing.notificationSettings") }}</router-link>{{ $t("billing.walletImportantPoint3After") }}</span></div>
                        <div class="ar-point"><span>📋</span><span>{{ $t("billing.walletImportantPoint4") }}</span></div>
                        <div class="ar-point"><span>∞</span><span>{{ $t("billing.walletImportantPoint5") }}</span></div>
                    </div>
                </div>
            </div>

            <div class="ar-card">
                <div class="ar-card-head">
                    <h3>{{ $t("billing.autoBalanceReload") }}</h3>
                    <p>{{ $t("billing.autoBalanceReloadDesc") }}</p>
                </div>
                <div class="ar-rows">
                    <div class="ar-row">
                        <div class="ar-lbl"><label>{{ $t("billing.autoReloadToggle") }}</label><span>{{ $t("billing.autoReloadToggleDesc") }}</span></div>
                        <div class="ar-ctrl">
                            <a-switch v-model="autoRebalanceSettings.enabled" @change="onAutoRebalanceEnabledChange" />
                            <span v-if="autoRebalanceSettings.disabledDueToFailure" class="ar-fail">{{ $t("billing.disabledDueToFailure") }}</span>
                        </div>
                    </div>
                    <div class="ar-row" v-if="autoRebalanceSettings.enabled">
                        <div class="ar-lbl"><label>{{ $t("billing.thresholdValue") }}</label><span>{{ $t("billing.thresholdValueDesc") }}</span></div>
                        <div class="ar-ctrl"><a-input-number v-model="autoRebalanceSettings.threshold" :min="0" :max="5000" :step="1" style="width:150px" @change="onSettingsChange" /></div>
                    </div>
                    <div class="ar-row" v-if="autoRebalanceSettings.enabled">
                        <div class="ar-lbl"><label>{{ $t("billing.reloadMode") }}</label><span>{{ $t("billing.reloadModeDesc") }}</span></div>
                        <div class="ar-ctrl ar-ctrl-col">
                            <a-radio-group v-model="autoRebalanceSettings.mode" @change="onSettingsChange">
                                <a-radio value="add">{{ $t("billing.addFixedAmount") }}</a-radio>
                                <a-radio value="fixedBalance">{{ $t("billing.fixBalance") }}</a-radio>
                            </a-radio-group>
                            <div class="ar-mode-hint"><span class="material-icons">info</span><span v-if="autoRebalanceSettings.mode === 'add'">{{ $t("billing.modeAddDesc") }}</span><span v-else>{{ $t("billing.modeFixedDesc") }}</span></div>
                        </div>
                    </div>
                    <div class="ar-row" v-if="autoRebalanceSettings.enabled">
                        <div class="ar-lbl"><label>{{ autoRebalanceSettings.mode === 'add' ? $t("billing.amountToAdd") : $t("billing.targetBalance") }}</label><span>{{ autoRebalanceSettings.mode === 'add' ? $t("billing.amountToAddDesc") : $t("billing.targetBalanceDesc") }}</span></div>
                        <div class="ar-ctrl"><a-input-number v-model="autoRebalanceSettings.upTo" :min="autoRebalanceSettings.mode === 'fixedBalance' ? autoRebalanceSettings.threshold + 1 : 1" :max="10000" :step="10" style="width:150px" @change="onSettingsChange" /></div>
                    </div>
                    <div class="ar-row" v-if="autoRebalanceSettings.enabled">
                        <div class="ar-lbl"><label>{{ $t("billing.panelNotifications") }}</label><span>{{ $t("billing.panelNotificationsDesc") }}</span></div>
                        <div class="ar-ctrl ar-ctrl-col">
                            <a-checkbox v-model="autoRebalanceSettings.settings.notifyOnRecharge" @change="onSettingsChange">{{ $t("billing.notifyOnRecharge") }}</a-checkbox>
                            <a-checkbox v-model="autoRebalanceSettings.settings.notifyOnFailure" @change="onSettingsChange">{{ $t("billing.notifyOnFailure") }}</a-checkbox>
                            <a-checkbox v-model="autoRebalanceSettings.settings.notifyOnDisabled" @change="onSettingsChange">{{ $t("billing.notifyOnDisabled") }}</a-checkbox>
                        </div>
                    </div>
                    <div class="ar-row" v-if="autoRebalanceSettings.enabled">
                        <div class="ar-lbl"><label>{{ $t("billing.emailNotifications") }}</label><span>{{ $t("billing.emailNotificationsDesc") }}</span></div>
                        <div class="ar-ctrl ar-ctrl-col">
                            <a-select mode="tags" v-model="autoRebalanceSettings.settings.emailRecipients" :placeholder="$t('billing.emailPlaceholder')" style="width:300px" @change="onSettingsChange" :tokenSeparators="[',', ';', ' ']" />
                            <div class="ar-email-hint"><span class="material-icons">info</span>{{ $t("billing.emailHint") }}</div>
                        </div>
                    </div>
                    <div class="ar-status" v-if="autoRebalanceSettings.enabled">
                        <div class="ar-status-row"><span>{{ $t("billing.lastTrigger") }}</span><span>{{ autoRebalanceSettings.lastTriggeredAt ? formatDate(autoRebalanceSettings.lastTriggeredAt) : $t("billing.neverTriggered") }}</span></div>
                        <div class="ar-status-row" v-if="autoRebalanceSettings.failedAttempts > 0"><span>{{ $t("billing.failedAttempt") }}</span><span class="ar-fail">{{ autoRebalanceSettings.failedAttempts }}/{{ autoRebalanceSettings.maxFailedAttempts }}</span></div>
                    </div>
                    <div class="ar-actions">
                        <SupsisButton :primary="true" :disabled="savingSettings" @click="saveAutoRebalanceSettings">{{ savingSettings ? '...' : $t("billing.saveSettings") }}</SupsisButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import log2 from "../../../../../utils/log2";
import WalletAddBalance from "../../components/WalletAddBalance.vue";
import CreditCardNeeded from "../../../../../components/billing/CreditCardNeeded.vue";
import AutoRebalancePromoModal from "../../components/AutoRebalancePromoModal.vue";
import moment from "moment";
import MoneyFormatter from "../../../../../components/MoneyFormatter.vue";
import _ from "lodash";
import NewSupsisTable from "../../../../../components/common/NewSupsisTable.vue";
import SupsisButton from "../../components/SupsisButton.vue";
import FilterChip from "../../../../../components/popup-chat/chat-window/FilterChip.vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import { getAutoRebalanceSettings, updateAutoRebalanceSettings, testAutoRebalance, depositBalanceById } from "../../../../../api/site";

var log = log2("Settings/Wallet");

export default {
    name: "Wallet",
    components: { MoneyFormatter, WalletAddBalance, CreditCardNeeded, AutoRebalancePromoModal, NewSupsisTable, SupsisButton, FilterChip },
    data: function () {
        return {
            addBalanceModal: false,
            creditCardNeededModal: false,
            showAutoRebalancePromo: false,
            activeTab: "all",
            activeMainTab: "transactions",
            currentPageInput: 1,
            selectedTypes: [],
            dateOperator: "between",
            selectedChannelIds: [],
            selectedRefs: [],
            savingSettings: false,
            testingAutoRebalance: false,
            settingsChanged: false,
            autoRebalanceSettings: {
                enabled: false, threshold: 0, upTo: 100, mode: 'fixedBalance',
                lastTriggeredAt: null, failedAttempts: 0, lastFailedAt: null, maxFailedAttempts: 3, disabledDueToFailure: false,
                settings: { cooldownMinutes: 10, notifyOnRecharge: true, notifyOnFailure: true, notifyOnDisabled: true, emailRecipients: [] }
            },
            filters: { page: 1, perPage: 100, type: "", startDate: null, endDate: null, channelId: "", product: "", ref: "" },
            productTypeMapping: {
                "whatsapp-business-api": ["deposit","business-marketing","business-utility","business-authentication","business-service","incoming-session-message","outgoing-session-message","free-tier-conversation","business-referral-conversion"],
                "voice-agent": ["voice-agent-web-call-outbound","voice-agent-phone-call-inbound","voice-agent-phone-call-outbound"],
                "ai-usage": ["ai-usage","rag-query-rewrite","ai-rag-rerank"],
                "automation": ["cloud-convert","speech-to-text","crawler","google-search","custom"]
            },
        };
    },
    computed: {
        ...mapState({
            isAdmin: function (s) { return s.auth.adminUser !== null; },
            wallet: function (s) { return s.wallet.wallet; },
            activities: function (s) { return s.wallet.activities; },
            totalActivity: function (s) { return s.wallet.totalActivity; },
            billingDetail: function (s) { return s.site.billingDetail; },
            settings: function (s) { return s.setting.settings; },
            site: function (s) { return s.site.site; },
            channels: function (s) {
                return s.channel.channels.filter(function (c) { return ["whatsapp-business-api","whatsapp-business-api-supsis","whatsapp-qr","whatsapp-v2"].indexOf(c.type) !== -1; });
            },
        }),
        dateRange: function () {
            if (this.filters.startDate && this.filters.endDate) return [this.filters.startDate, this.filters.endDate];
            return [];
        },
        dateFilterValue: function () {
            if (this.filters.startDate && this.filters.endDate) return [this.filters.startDate, this.filters.endDate];
            return null;
        },
        channelFilterOptions: function () {
            var channelIcons = {
                "whatsapp-business-api": "WhatsappBusinessIcon",
                "whatsapp-business-api-supsis": "WhatsappBusinessIcon",
                "whatsapp-qr": "WhatsappV2",
                "whatsapp-v2": "WhatsappV2"
            };
            var channelGroups = {
                "whatsapp-business-api": "WhatsApp Business API",
                "whatsapp-business-api-supsis": "WhatsApp Business API",
                "whatsapp-qr": "WhatsApp QR",
                "whatsapp-v2": "WhatsApp V2"
            };
            return this.channels.map(function (ch) {
                return {
                    value: ch._id,
                    label: ch.name,
                    icon: channelIcons[ch.type] || "WhatsappBusinessIcon",
                    iconType: "component",
                    group: channelGroups[ch.type] || ch.type
                };
            });
        },
        refOptions: function () {
            if (this.activeTab !== "ai-usage") return [];
            var res = this.aiUsageResources;
            var iconMap = {
                "chatbot-train-website": "school",
                "chatbot-ai-assistant": "smart_toy",
                "recommend-call-function": "functions",
                "response-api": "api",
                "chat-completion": "forum",
                "automation_javascript": "code",
                "ask-ai": "help",
                "ai-bot-response": "smart_toy",
                "ai-classification": "label",
                "ai-random-text": "notes",
                "fixGrammar": "spellcheck",
                "rewriteAsLongSentence": "subject",
                "changeTone": "tune",
                "generateResponse": "quickreply",
                "ticketAiResponse": "confirmation_number",
                "translation": "translate",
                "promptToGenerateResponse": "edit_note",
                "mailAiresponse": "mail",
                "mailPromtMessage": "drafts",
                "generateSessionRecommendations": "recommend",
                "createWorkspace": "workspaces",
                "createTableReports": "assessment",
                "createFormula": "calculate",
                "table-assistant": "table_chart",
                "voice-agent": "record_voice_over",
                "javascript-generation": "code",
                "table-ai-field": "view_column",
                "summarize": "summarize",
                "sentiment-analysis": "mood",
                "rag-query-rewrite": "find_replace",
                "chatbot-javascript-generation": "code",
                "filter-suggestion": "filter_alt",
                "chatbot-ai": "smart_toy",
            };
            var keys = Object.keys(res);
            return keys.map(function (k) { return { value: k, label: res[k] || k, icon: iconMap[k] || "psychology" }; });
        },
        productOptions: function () {
            this.$i18n.locale;
            return [
                { value: "all", label: this.$t("billing.tabAll") },
                { value: "whatsapp-business-api", label: this.$t("auto.walletTabWhatsapp") },
                { value: "ai-usage", label: this.$t("billing.tabAiUsage") },
                { value: "voice-agent", label: this.$t("billing.tabVoiceAgent") },
                { value: "automation", label: this.$t("billing.tabAutomation") },
            ];
        },
        typeOptions: function () {
            if (this.activeTab === "all") return [];
            var mapping = this.productTypeMapping[this.activeTab] || [];
            var self = this;
            var typeIconMap = {
                "deposit": "account_balance",
                "business-marketing": "campaign",
                "business-utility": "build",
                "business-authentication": "lock",
                "business-service": "support_agent",
                "incoming-session-message": "call_received",
                "outgoing-session-message": "call_made",
                "free-tier-conversation": "money_off",
                "business-referral-conversion": "people",
                "voice-agent-web-call-outbound": "phone_forwarded",
                "voice-agent-phone-call-inbound": "phone_callback",
                "voice-agent-phone-call-outbound": "phone_forwarded",
                "ai-usage": "psychology",
                "rag-query-rewrite": "find_replace",
                "ai-rag-rerank": "sort",
                "cloud-convert": "cloud_sync",
                "speech-to-text": "mic",
                "crawler": "travel_explore",
                "google-search": "search",
                "custom": "extension",
            };
            return mapping.map(function (key) { return { value: key, label: self.types[key] || key, icon: typeIconMap[key] || "receipt" }; });
        },
        tableColumns: function () {
            this.$i18n.locale;
            var t = this.$t.bind(this);
            var tab = this.activeTab;
            var colDate = { key: 'createdAt', title: t("message.date"), width: 160, visible: true, selectable: false };
            var colType = { key: 'type', title: t("message.transactionType"), width: 170, visible: true, selectable: false };
            var colAmount = { key: 'amount', title: t("message.amount"), width: 120, visible: true };
            var colBefore = { key: 'before', title: t("message.previousBalance"), width: 120, visible: true };
            var colAfter = { key: 'after', title: t("message.nextBalance"), width: 120, visible: true };

            if (tab === "whatsapp-business-api") {
                return [
                    colDate, colType,
                    { key: 'phoneNumber', title: t("message.number"), width: 150, visible: true },
                    { key: 'country', title: t("message.country"), width: 100, visible: true },
                    colAmount, colBefore, colAfter
                ];
            }

            if (tab === "ai-usage") {
                return [
                    colDate, colType,
                    { key: 'aiResource', title: t("message.resource"), width: 170, visible: true },
                    { key: 'aiModel', title: t("message.model"), width: 140, visible: true },
                    { key: 'aiProvider', title: t("message.provider"), width: 120, visible: true },
                    { key: 'aiDetails', title: t("message.details"), width: 180, visible: false },
                    colAmount, colBefore, colAfter
                ];
            }

            if (tab === "voice-agent") {
                return [
                    colDate, colType,
                    { key: 'direction', title: t("message.direction"), width: 100, visible: true },
                    { key: 'fromNumber', title: t("message.from"), width: 130, visible: true },
                    { key: 'toNumber', title: t("message.to"), width: 130, visible: true },
                    colAmount, colBefore, colAfter
                ];
            }

            if (tab === "automation") return [colDate, colType, colAmount, colBefore, colAfter];

            return [
                colDate, colType,
                { key: 'phoneNumber', title: t("message.number"), width: 150, visible: true },
                { key: 'country', title: t("message.country"), width: 100, visible: true },
                { key: 'aiResource', title: t("message.resource"), width: 170, visible: true },
                colAmount, colBefore, colAfter
            ];
        },
        types: function () {
            this.$i18n.locale;
            return {
                deposit: this.$t("message.deposit") || "Bakiye Yükleme",
                "business-marketing": this.$t("message.businessMarketing") || "Marketing",
                "business-utility": this.$t("message.businessUtility") || "Utility",
                "business-authentication": this.$t("message.businessAuthentication") || "OTP",
                "business-service": this.$t("message.businessService") || "Hizmet",
                "incoming-session-message": this.$t("message.incomingSessionMessage") || "Gelen Mesaj",
                "outgoing-session-message": this.$t("message.outgoingSessionMessage") || "Giden Mesaj",
                "free-tier-conversation": this.$t("message.freeTierConversation") || "Ücretsiz Görüşme",
                "business-referral-conversion": this.$t("message.businessReferralConversion") || "Referral Dönüşüm",
                message: this.$t("message.wpMessage") || "Whatsapp Toplu Mesaj",
                "voice-agent-phone-call-inbound": this.$t("auto.walletTypeVoiceAgentPhoneCallInbound"),
                "voice-agent-web-call-outbound": this.$t("auto.walletTypeVoiceAgentWebCallOutbound"),
                "voice-agent-phone-call-outbound": this.$t("auto.walletTypeVoiceAgentPhoneCallOutbound"),
                "ai-usage": this.$t("auto.walletTypeAiUsage"),
                custom: this.$t("auto.walletTypeCustom"),
                "cloud-convert": this.$t("auto.walletTypeCloudConvert"),
                "speech-to-text": this.$t("auto.walletTypeSpeechToText"),
                crawler: this.$t("auto.walletTypeCrawler"),
                "google-search": this.$t("auto.walletTypeGoogleSearch"),
                "rag-query-rewrite": this.$t("auto.walletTypeRagQueryRewrite"),
                "ai-rag-rerank": this.$t("auto.walletTypeAiRagRerank"),
            };
        },
        aiUsageResources: function () {
            this.$i18n.locale;
            return {
                "chatbot-train-website": this.$t("auto.walletAiChatbotTrainWebsite"), "chatbot-ai-assistant": this.$t("auto.walletAiChatbotAiAssistant"),
                "recommend-call-function": this.$t("auto.walletAiRecommendCallFunction"), "response-api": this.$t("auto.walletAiResponseApi"),
                "chat-completion": this.$t("auto.walletAiChatCompletion"), automation_javascript: this.$t("auto.walletAiAutomationJavascript"),
                "ask-ai": this.$t("auto.walletAiAskAi"), "ai-bot-response": this.$t("auto.walletAiBotResponse"),
                "ai-classification": this.$t("auto.walletAiClassification"), "ai-random-text": this.$t("auto.walletAiRandomText"),
                fixGrammar: this.$t("auto.walletAiFixGrammar"), rewriteAsLongSentence: this.$t("auto.walletAiRewriteAsLongSentence"),
                changeTone: this.$t("auto.walletAiChangeTone"), generateResponse: this.$t("auto.walletAiGenerateResponse"),
                ticketAiResponse: this.$t("auto.walletAiTicketAiResponse"), translation: this.$t("auto.walletAiTranslation"),
                promptToGenerateResponse: this.$t("auto.walletAiPromptToGenerateResponse"), mailAiresponse: this.$t("auto.walletAiMailAiResponse"),
                mailPromtMessage: this.$t("auto.walletAiMailPromptMessage"), generateSessionRecommendations: this.$t("auto.walletAiGenerateSessionRecommendations"),
                createWorkspace: this.$t("auto.walletAiCreateWorkspace"), createTableReports: this.$t("auto.walletAiCreateTableReports"),
                createFormula: this.$t("auto.walletAiCreateFormula"), "table-assistant": this.$t("auto.walletAiTableAssistant"),
                "voice-agent": this.$t("auto.walletAiVoiceAssistant"), "javascript-generation": this.$t("auto.walletAiJavascriptGeneration"),
                "table-ai-field": this.$t("auto.walletAiTableAiField"), summarize: this.$t("auto.walletAiSummarize"),
                "sentiment-analysis": this.$t("auto.walletAiSentimentAnalysis"), "rag-query-rewrite": this.$t("auto.walletAiRagQueryRewriter"),
                "chatbot-javascript-generation": this.$t("auto.walletAiChatbotJavascriptGeneration"),
                "filter-suggestion": this.$t("auto.walletAiFilterSuggestion"),
                "chatbot-ai": this.$t("auto.walletAiChatbotAi"),
            };
        },
        totalPages: function () { return Math.ceil(this.totalActivity / this.filters.perPage); },
    },
    methods: {
        ...mapActions({ getBalance: "wallet/getBalance", getActivities: "wallet/getWalletLogs", addBalance: "wallet/addBalance" }),
        onProductChange: function (val) {
            this.activeTab = val;
            this.filters.product = val === "all" ? "" : val;
            this.selectedTypes = [];
            this.filters.type = "";
            this.selectedRefs = [];
            this.filters.ref = "";
            this.resetPage();
        },
        onTypesChange: function (vals) {
            this.selectedTypes = vals;
            this.filters.type = vals.join(',');
            this.resetPage();
        },
        onTypesFilterChange: function (vals) {
            this.selectedTypes = Array.isArray(vals) ? vals : (vals ? [vals] : []);
            this.filters.type = this.selectedTypes.join(',');
            this.resetPage();
        },
        onTypesFilterClear: function () { this.selectedTypes = []; this.filters.type = ""; },
        onRefsChange: function (vals) {
            this.selectedRefs = Array.isArray(vals) ? vals : (vals ? [vals] : []);
            this.filters.ref = this.selectedRefs.join(',');
            this.resetPage();
        },
        onRefsClear: function () { this.selectedRefs = []; this.filters.ref = ""; },
        onDateRangeChange: function (dates) {
            if (dates && dates.length === 2) { this.filters.startDate = dates[0]; this.filters.endDate = dates[1]; }
            else { this.filters.startDate = null; this.filters.endDate = null; }
        },
        onDateFilterChange: function (val) {
            if (!val) { this.filters.startDate = null; this.filters.endDate = null; return; }
            var op = this.dateOperator;
            if (op === "between") {
                if (Array.isArray(val) && val.length === 2) {
                    this.filters.startDate = val[0]; this.filters.endDate = val[1];
                } else if (typeof val === 'string') {
                    var range = this.getDateRangeFromPreset(val);
                    if (range) { this.filters.startDate = range[0]; this.filters.endDate = range[1]; }
                }
            } else if (op === "greater_than" || op === "greater_than_or_equal") {
                this.filters.startDate = val; this.filters.endDate = null;
            } else if (op === "less_than" || op === "less_than_or_equal") {
                this.filters.startDate = null; this.filters.endDate = val;
            } else if (op === "equals") {
                this.filters.startDate = moment(val).startOf("day"); this.filters.endDate = moment(val).endOf("day");
            }
        },
        onDateOperatorChange: function (op) { this.dateOperator = op; },
        onDateFilterClear: function () { this.filters.startDate = null; this.filters.endDate = null; },
        onChannelFilterChange: function (val) {
            if (Array.isArray(val)) {
                this.selectedChannelIds = val;
                this.filters.channelId = val.join(',');
            } else {
                this.selectedChannelIds = val ? [val] : [];
                this.filters.channelId = val || "";
            }
        },
        onChannelFilterClear: function () { this.selectedChannelIds = []; this.filters.channelId = ""; },
        getDateRangeFromPreset: function (preset) {
            if (!preset) return null;
            var m = moment;
            switch (preset) {
                case "today": return [m().startOf("day"), m().endOf("day")];
                case "yesterday": return [m().subtract(1, "day").startOf("day"), m().subtract(1, "day").endOf("day")];
                case "thisWeek": return [m().startOf("isoWeek"), m().endOf("isoWeek")];
                case "lastWeek": return [m().subtract(1, "week").startOf("isoWeek"), m().subtract(1, "week").endOf("isoWeek")];
                case "last7Days": return [m().subtract(7, "day").startOf("day"), m().endOf("day")];
                case "last30Days": return [m().subtract(30, "day").startOf("day"), m().endOf("day")];
                case "thisMonth": return [m().startOf("month"), m().endOf("month")];
                case "lastMonth": return [m().subtract(1, "month").startOf("month"), m().subtract(1, "month").endOf("month")];
                default:
                    if (typeof preset === 'string' && preset.indexOf("month") === 0) {
                        var i = parseInt(preset.replace("month", ""), 10);
                        if (i) return [m().subtract(i, "month").startOf("month"), m().subtract(i, "month").endOf("month")];
                    }
                    return null;
            }
        },
        getPhoneNumber: function (item) {
            var n = _.get(item, "payload.phoneNumber", _.get(item, "payload.to_number"));
            if (!n) return "-";
            return typeof n === "object" ? (_.get(n, "number") || "-") : (n || "-");
        },
        getAIUsageResource: function (item) { var r = _.get(item, "payload.ref", "-"); return this.aiUsageResources[r] || r; },
        formatDate: function (d) { return moment(d).format("YYYY/MM/DD HH:mm:ss"); },
        formatType: function (type, item) {
            if (type === "deposit" && item && item.payload && item.payload.autoRebalance) return this.$t("billing.autoBalanceReload");
            return this.types[type] || type;
        },
        seperateWithCommas: function (x) { return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","); },
        formatPrice: function (p) { return this.seperateWithCommas(p.toFixed(2)); },
        formatTokenCount: function (t) { if (!t) return "0"; if (t >= 1e6) return (t/1e6).toFixed(1)+"M"; if (t >= 1e3) return (t/1e3).toFixed(1)+"K"; return t.toString(); },
        openAddWhatsappBalanceModal: function () {
            if (this.isAdmin) { this.addBalanceModal = true; return; }
            if (!this.billingDetail || !this.billingDetail.binNumber) { this.creditCardNeededModal = true; } else { this.addBalanceModal = true; }
        },
        addBalanceWhatsapp: function (price, invoicePayload) {
            var self = this;
            var p = this.isAdmin
                ? depositBalanceById({ siteId: this.site._id, walletType: "whatsapp", amount: price, invoicePayload: invoicePayload })
                : this.addBalance({ walletType: "whatsapp", amount: price, invoicePayload: invoicePayload });
            p.then(function (r) {
                if (r.status === "error") { self.$toasted.error(r.message, { duration: 3000, position: "top-center" }); return; }
                if (self.isAdmin) self.getBalance();
                self.addBalanceModal = false;
                self.$toasted.success(self.$t("billing.balanceAddedSuccess", { price: price }), { duration: 3000, position: "top-center" });
                sendSocketMessage("user wallet-charge", { siteId: self.site._id });
                self.checkAndShowAutoRebalancePromo();
            }).catch(function () { self.$message.error({ content: self.$t("billing.balanceAddFailed"), key: "markpaid", duration: 3 }); });
        },
        checkAndShowAutoRebalancePromo: function () { var self = this; if (!localStorage.getItem("autoRebalancePromoShown")) setTimeout(function () { self.showAutoRebalancePromo = true; }, 500); },
        handleAutoRebalancePromoActivate: function (d) { this.showAutoRebalancePromo = false; if (d) localStorage.setItem("autoRebalancePromoShown", "true"); this.activeMainTab = "auto-rebalance"; },
        handleAutoRebalancePromoClose: function (d) { this.showAutoRebalancePromo = false; if (d) localStorage.setItem("autoRebalancePromoShown", "true"); },
        jumpToPage: function () { if (this.currentPageInput >= 1 && this.currentPageInput <= this.totalPages) this.filters.page = this.currentPageInput; else this.currentPageInput = this.filters.page; },
        resetPage: function () { this.filters.page = 1; this.currentPageInput = 1; },
        getWalletActivities: function () { this.getActivities(this.filters); },
        backwardPage: function () { if (this.filters.page <= 1) return; this.filters.page--; this.currentPageInput = this.filters.page; },
        forwardPage: function () { if (this.filters.page >= this.totalPages) return; this.filters.page++; this.currentPageInput = this.filters.page; },
        loadAutoRebalanceSettings: async function () {
            try { var r = await getAutoRebalanceSettings("whatsapp"); if (r && r.autoRebalance) { this.autoRebalanceSettings = Object.assign({}, r.autoRebalance); if (!this.autoRebalanceSettings.mode) this.autoRebalanceSettings.mode = 'fixedBalance'; } }
            catch (e) { console.error(e); this.$message.error(this.$t("billing.autoRebalanceLoadError")); }
        },
        saveAutoRebalanceSettings: async function () {
            this.savingSettings = true;
            try {
                if (this.autoRebalanceSettings.enabled) {
                    if (this.autoRebalanceSettings.threshold < 0) { this.$message.error(this.$t("billing.thresholdMinError")); this.savingSettings = false; return; }
                    if (this.autoRebalanceSettings.upTo <= 0) { this.$message.error(this.$t("billing.targetBalanceMinError")); this.savingSettings = false; return; }
                    if (this.autoRebalanceSettings.mode === 'fixedBalance' && this.autoRebalanceSettings.upTo <= this.autoRebalanceSettings.threshold) { this.$message.error(this.$t("billing.targetBalanceThresholdError")); this.savingSettings = false; return; }
                }
                await updateAutoRebalanceSettings("whatsapp", { enabled: this.autoRebalanceSettings.enabled, threshold: this.autoRebalanceSettings.threshold, upTo: this.autoRebalanceSettings.upTo, mode: this.autoRebalanceSettings.mode, settings: this.autoRebalanceSettings.settings });
                this.$message.success(this.$t("billing.autoRebalanceSaveSuccess")); this.settingsChanged = false; await this.loadAutoRebalanceSettings();
            } catch (e) { console.error(e); this.$message.error(e.response && e.response.data && e.response.data.error ? e.response.data.error : this.$t("billing.settingsSaveFailed")); }
            finally { this.savingSettings = false; }
        },
        testAutoRebalance: async function () {
            this.testingAutoRebalance = true;
            try { var r = await testAutoRebalance("whatsapp"); var type = r.status === "would_trigger" ? "warning" : r.status === "not_needed" ? "success" : "info"; this.$message[type](r.message || "Test tamamlandı"); if (r.note) this.$message.info(r.note); }
            catch (e) { console.error(e); this.$message.error(e.response && e.response.data && e.response.data.error ? e.response.data.error : this.$t("billing.testFailed")); }
            finally { this.testingAutoRebalance = false; }
        },
        onAutoRebalanceEnabledChange: function (v) {
            this.settingsChanged = true;
            if (v && (!this.billingDetail || !this.billingDetail.binNumber)) { this.autoRebalanceSettings.enabled = false; this.creditCardNeededModal = true; this.$message.warning(this.$t("billing.creditCardRequiredForAutoReload")); }
        },
        onSettingsChange: function () { this.settingsChanged = true; },
    },
    mounted: function () { this.getWalletActivities(); this.loadAutoRebalanceSettings(); },
    watch: {
        filters: {
            deep: true,
            handler: function (n, o) {
                if (o && (n.type !== o.type || n.channelId !== o.channelId || n.startDate !== o.startDate || n.endDate !== o.endDate || n.product !== o.product || n.ref !== o.ref)) this.resetPage();
                this.getWalletActivities();
            },
        },
    },
};
</script>

<style lang="scss">
#wallet-settings {
    .ant-input-number { border-radius: 6px; border-color: #d5d6e3; &:hover,&:focus { border-color: #46499D; } .ant-input-number-handler-wrap { border-radius: 0 6px 6px 0; } }
    .pg-input .ant-input-number-handler-wrap { display: none; }
    .ant-calendar-picker .ant-calendar-picker-input { border-radius: 6px; border-color: #d5d6e3; font-size: 13px; &:hover,&:focus { border-color: #46499D; } }
    .ant-select-selection { border-radius: 6px; border-color: #d5d6e3; &:hover,&:focus { border-color: #46499D; } }
    .ant-switch-checked { background-color: #46499D; }
    .ant-radio-checked .ant-radio-inner { border-color: #46499D; &::after { background-color: #46499D; } }
    .ant-checkbox-checked .ant-checkbox-inner { background-color: #46499D; border-color: #46499D; }
}
</style>

<style scoped lang="scss">
$pr: #46499D;
$pr-lt: #edeef8;
$pr-hv: #393c84;
$pr-sf: #d5d6ed;
$t1: #1e1e2e;
$t2: #555568;
$t3: #8e8ea0;
$bd: #e4e4ec;
$bg: #f7f7fb;
$wh: #ffffff;
$red: #e74c3c;

/* ===== 1) BALANCE CARD ===== */
.balance-card {
    display: flex; align-items: center; justify-content: space-between;
    background: $wh; border: 1px solid $bd; border-left: 4px solid $pr;
    border-radius: 10px; padding: 20px 24px;
    .balance-left { display: flex; align-items: center; gap: 16px; }
    .balance-icon {
        display: flex; align-items: center; justify-content: center;
        width: 52px; height: 52px; background: $pr-lt; border-radius: 12px; flex-shrink: 0;
        .material-icons { font-size: 28px; color: $pr; }
    }
    .balance-text { display: flex; flex-direction: column; gap: 2px; }
    .balance-label { font-size: 13px; font-weight: 500; color: $t3; text-transform: uppercase; letter-spacing: .5px; }
    .balance-amount { font-size: 26px; font-weight: 700; color: $t1; letter-spacing: -.5px; }
    .supsis-button.primary {
        background-color: $pr; border-color: $pr; font-size: 14px;
        height: 40px; padding: 0 20px; border-radius: 8px;
        &:hover { background-color: $pr-hv; border-color: $pr-hv; }
    }
}

/* ===== 2) UNDERLINE TABS ===== */
.underline-tabs {
    display: flex; gap: 0; margin-top: 24px; border-bottom: 2px solid $bd;
    .u-tab {
        display: flex; align-items: center; gap: 6px;
        padding: 12px 20px; font-size: 14px; font-weight: 500; color: $t3;
        cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px;
        transition: all .2s;
        .material-icons { font-size: 18px; }
        &:hover { color: $t1; }
        &.active { color: $pr; border-bottom-color: $pr; font-weight: 600; }
    }
}

/* ===== 3) TX PANEL ===== */
.tx-panel { margin-top: 20px; display: flex; flex-direction: column; }

/* ===== FILTER BAR ===== */
.filter-bar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 0 8px; gap: 12px; flex-wrap: wrap;

    .product-pills {
        display: flex; gap: 3px; background: $bg; padding: 3px; border-radius: 7px;
        .pill {
            padding: 5px 14px; border-radius: 5px; font-size: 13px; font-weight: 500;
            color: $t2; cursor: pointer; transition: all .18s; white-space: nowrap;
            &:hover { background: darken($bg, 4%); color: $t1; }
            &.active { background: $wh; color: $pr; font-weight: 600; box-shadow: 0 1px 3px rgba(0,0,0,.07); }
        }
    }

}


/* ===== TABLE TOOLBAR ===== */
.table-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 14px; background: $bg; border: 1px solid $bd;
    border-radius: 8px 8px 0 0; gap: 12px; flex-wrap: wrap;
    .toolbar-left { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
    .toolbar-right {
        display: flex; align-items: center; gap: 14px;
        .pg-info { font-size: 13px; color: $t2; white-space: nowrap; }
        .pg-controls {
            display: flex; align-items: center; gap: 5px;
            .pg-input { width: 48px; text-align: center; }
            .pg-of { font-size: 13px; color: $t3; }
            .supsis-button { width: 28px; height: 28px; min-width: 28px; border-color: $bd; &:hover:not(.disabled) { border-color: $pr; color: $pr; } }
        }
    }
}

/* ===== TABLE AREA ===== */
.table-area {
    border: 1px solid $bd; border-top: none; border-radius: 0 0 8px 8px;
    overflow: auto;
    flex: 1; min-height: 200px; max-height: 520px;

    .cell-date { font-size: 13px; color: $t2; font-variant-numeric: tabular-nums; }
    .ai-resource { font-size: 13px; }
    .model-chip { display: inline-block; padding: 1px 7px; background: $pr-lt; color: $pr; border-radius: 4px; font-size: 12px; font-weight: 500; }
    .dir-badge {
        display: inline-flex; align-items: center; gap: 2px; font-size: 12px; font-weight: 500; padding: 1px 7px; border-radius: 4px;
        .material-icons { font-size: 14px; }
        &.in { background: #e8f5e9; color: #2e7d32; }
        &.out { background: #e3f2fd; color: #1565c0; }
    }
    .ai-detail-wrap { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; .tok { font-size: 12px; color: $t3; } }
}

/* ===== AUTO REBALANCE ===== */
.ar-panel { margin-top: 20px; }

.ar-alert {
    display: flex; align-items: flex-start; gap: 12px; padding: 14px 18px;
    background: #fef9eb; border: 1px solid #f5e6b8; border-radius: 8px; margin-bottom: 20px;
    .ar-alert-icon { font-size: 20px; color: #d4a017; flex-shrink: 0; margin-top: 1px; }
    .ar-alert-body { flex: 1; }
    .ar-alert-title { font-weight: 600; font-size: 14px; color: #8a6d1b; margin-bottom: 8px; }
    .ar-point { display: flex; gap: 8px; margin-bottom: 5px; font-size: 13px; line-height: 1.5; color: #6b5517; &:last-child { margin-bottom: 0; } }
    .ar-link { color: $pr; text-decoration: none; font-weight: 500; &:hover { text-decoration: underline; } }
}

.ar-card {
    background: $wh; border: 1px solid $bd; border-radius: 10px; padding: 24px;
    .ar-card-head { margin-bottom: 20px; padding-bottom: 14px; border-bottom: 1px solid $bd; h3 { margin: 0 0 4px; font-size: 17px; font-weight: 600; color: $t1; } p { margin: 0; font-size: 13px; color: $t2; } }
}

.ar-rows {
    .ar-row {
        display: flex; justify-content: space-between; align-items: flex-start;
        padding: 16px 0; border-bottom: 1px solid lighten($bd, 3%);
        &:first-child { padding-top: 0; } &:last-child { border-bottom: none; }
    }
    .ar-lbl {
        flex: 1; margin-right: 20px;
        label { display: block; font-weight: 600; color: $t1; font-size: 14px; margin-bottom: 2px; }
        span { font-size: 13px; color: $t3; line-height: 1.4; }
    }
    .ar-ctrl { display: flex; align-items: center; gap: 10px; }
    .ar-ctrl-col { flex-direction: column; align-items: flex-start; gap: 8px; }
    .ar-fail { font-size: 12px; color: $red; font-weight: 500; }
    .ar-mode-hint {
        display: flex; align-items: flex-start; gap: 6px; padding: 8px 12px;
        background: $pr-lt; border: 1px solid $pr-sf; border-radius: 6px; font-size: 13px; color: $t2; line-height: 1.4;
        .material-icons { font-size: 16px; color: $pr; margin-top: 1px; flex-shrink: 0; }
    }
    .ar-email-hint { display: flex; align-items: center; gap: 5px; font-size: 12px; color: $t3; font-style: italic; .material-icons { font-size: 14px; } }
    .ar-status {
        margin-top: 14px; padding: 12px 14px; background: $bg; border-radius: 8px; border: 1px solid $bd;
        .ar-status-row { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 6px; &:last-child { margin-bottom: 0; } span:first-child { color: $t2; font-weight: 500; } span:last-child { color: $t1; } }
    }
    .ar-actions {
        margin-top: 18px; padding-top: 16px; border-top: 1px solid $bd; display: flex; gap: 10px;
        .supsis-button.primary { background-color: $pr; border-color: $pr; border-radius: 8px; height: 38px; &:hover { background-color: $pr-hv; border-color: $pr-hv; } }
    }
}
</style>
